import {
  Map as MapboxMap,
  NavigationControl,
  ScaleControl,
  Popup,
  GeolocateControl,
} from 'mapbox-gl';
import {
  CENTER_SMALL,
  CENTER_LARGE,
  INITIAL_ZOOM_LEVEL,
  TILESET,
  STYLE,
  SOURCE,
  SOURCE_LAYER,
  TOKEN,
} from './maputils/constants';
import { geocoder } from './maputils/geocoder';
import { renderTooltip } from './maputils/tooltip';

import { renderSvelteComponent } from './utils/render.js';
import { loadJsonScript } from './utils/load-json-script';
import Building from './components/Building.svelte';

import {
  COMPLETE,
  COMPLETE_BORDER,
  INCOMPLETE,
  INCOMPLETE_BORDER,
} from './maputils/colors';

let w = window.innerWidth;
// const bounds = w < 500 ? BOUNDS_SMALL : BOUNDS_LARGE;

const center = w < 500 ? CENTER_SMALL : CENTER_LARGE;

/**
 *
 * @param {Element} container
 */
export async function render(container, selectedCity) {
  // building results container
  const buildingContainer = document.getElementById('building-results-card');
  // city data and results container
  const cityToplines = loadJsonScript('city-toplines');

  // create our popup but don't add to the map for now
  const popup = new Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 8,
  });

  // create our map - options you can add https://docs.mapbox.com/mapbox-gl-js/api/map/
  const map = new MapboxMap({
    accessToken: TOKEN,
    container,
    style: STYLE,
    // bounds,
    maxBounds: [-118.955854, 33.690791, -117.728976, 34.3198969],
    zoom: INITIAL_ZOOM_LEVEL,
    center,
    cooperativeGestures: true,
  });

  // Add controls to the map
  map.addControl(new NavigationControl({ showCompass: false }));
  map.addControl(new ScaleControl({ unit: 'imperial' }), 'bottom-right');

  // disable map zoom when using scroll
  // map.scrollZoom.disable();
  // disable map rotation using right click + drag
  map.dragRotate.disable();
  // disable map rotation using touch rotation gesture
  map.touchZoomRotate.disableRotation();

  // Add geolocate control to the map.
  map.addControl(
    new GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      // When active the map will receive updates to the device's location as it changes.
      trackUserLocation: true,
      // Draw an arrow next to the location dot to indicate which direction the device is heading.
      showUserHeading: true,
    })
  );

  // we wait for it to fully load so we can add sources and layers
  await map.once('load');

  // add our data source
  map.addSource(SOURCE, {
    type: 'vector',
    url: TILESET,
    // promoteId: 'some unique id in tileset like precinctID', // use this for most cases and comment out genreated ID below
    generatedId: false, // use this if your tileset was generated and uploaded using tippecanoe
  });

  // Add a layer showing points as circles
  map.addLayer(
    {
      'id': 'points',

      // FILTER OUT LA POINTS
      // 'filter': [
      //   'all',
      //   [
      //     'match',
      //     [
      //       'get',
      //       'ordinance_city'
      //     ],
      //     [
      //       'Los Angeles',
      //       'Pasadena'
      //     ],
      //     false,
      //     true
      //   ]
      // ],    
      'type': 'circle',
      'source': SOURCE,
      'source-layer': SOURCE_LAYER,      
      'paint': {
        'circle-color': [
          'case',
          ['==', ['get', 'is_retrofitted'], true],
          COMPLETE,
          ['==', ['get', 'is_retrofitted'], false],
          INCOMPLETE,
          'rgba(0, 0, 0, 0)',
        ],
        'circle-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0,
          0.25,
          11.95,
          0.3,
          16,
          0.6,
        ],
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0,
          0.25,
          12,
          2.5,
          16,
          6,
          18,
          8,
        ],
        'circle-blur': ['interpolate', ['linear'], ['zoom'], 0, 1, 13, 0],
        'circle-stroke-color': [
          'case',
          ['==', ['get', 'is_retrofitted'], true],
          COMPLETE_BORDER,
          ['==', ['get', 'is_retrofitted'], false],
          INCOMPLETE_BORDER,
          'rgba(0, 0, 0, 0)',
        ],
      },
    },
    // Place points under labels, roads and buildings.
    'settlement-subdivision-label'
  );

  // render building results card
  function renderBuildingResults(props) {
    buildingContainer.innerHTML = '';
    renderSvelteComponent(Building, buildingContainer, {
      data: props,
    });
  }

  map.on('mouseenter', 'points', (e) => {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = 'pointer';

    // Copy coordinates array.
    const coordinates = e.features[0].geometry.coordinates.slice();

    // all the properties stored in the point
    const props = e.features[0].properties;

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    // go to render tooltip to parse out correct properties for display
    popup.setLngLat(coordinates).setHTML(renderTooltip(props)).addTo(map);

    map.on('click', 'points', (e) => {
      // render building results
      buildingContainer.innerHTML = '';
      renderSvelteComponent(Building, buildingContainer, {
        data: props,
      });
    });
  });

  map.on('mouseleave', 'points', () => {
    map.getCanvas().style.cursor = '';
    popup.remove();
  });

  let activeId;

  function setActiveId(id, params, lngLat) {
    if (activeId) {
      map.setFeatureState(
        { source: SOURCE, sourceLayer: SOURCE_LAYER, id: activeId },
        { hover: false }
      );
    }

    map.setFeatureState(
      { source: SOURCE, sourceLayer: SOURCE_LAYER, id },
      { hover: true }
    );
    activeId = id;
    map.getCanvas().style.cursor = 'default';

    // if parameters and lon/lat exists, add a tooltip
    if (params && lngLat) {
      popup.setLngLat(lngLat);
      popup.setHTML(renderTooltip(params)).addTo(map);
    }
  }

  function unsetActiveId() {
    if (activeId) {
      map.setFeatureState(
        { source: SOURCE, sourceLayer: SOURCE_LAYER, id: activeId },
        { hover: false }
      );

      activeId = undefined;
      map.getCanvas().style.cursor = '';
      popup.remove();
    }
  }

  map.on('mousemove', 'hover-layer', (e) => {
    const features = e.features;

    if (features.length > 0) {
      const feature = features[0];

      const featureId = feature.id;
      const props = feature.properties;

      setActiveId(featureId, props, e.lngLat);
    }
  });

  map.on('mouseleave', 'hover-layer', unsetActiveId);

  //Adding geocoder

  document.getElementById('geocoder').appendChild(geocoder.onAdd(map));

  geocoder.on('result', function (e) {
    map.on('moveend', function () {
      // returns array of data from geocoded address
      let result = e.result;

      // check if the result is in los angeles
      let resultContextArr = result.context;
      let result_place;

      resultContextArr.forEach((d) => {
        let rid = d.id;
        if (rid == 'place.192407788'){
          if (d.text == 'Los Angeles'){
            result_place = 'Los Angeles';
            return result_place;
          }
        }
      })


      // parse the address to match it with our map data

      let result_address = result.place_name;

      const streetregex = {
        Road: 'Rd.',
        Street: 'St.',
        Avenue: 'Ave.',
        Drive: 'Dr.',
        Court: 'Ct.',
        Boulevard: 'Blvd.',
        Lane: 'Ln.',
        Place: 'Pl.',
        Circle: 'Cir.',
      };

      Object.keys(streetregex).forEach((key) => {
        result_address = result_address.replaceAll(key, streetregex[key]);
      });

      result_address = result_address
        .replace(' California', '')
        .replace(', United States', '');

      // center the map on the geocoded address
      let point = map.project(result.center);

      // Set bbox around geocoded area
      const bbox = [
        [point.x - 100, point.y - 100],
        [point.x + 100, point.y + 100],
      ];

      // query nearby features
      var features = map.queryRenderedFeatures(bbox, {
        layers: ['points'],
      });

      // find a feature within the bbox with a matching address
      var feature = features.filter(function (a) {
        return a.properties.clean_address === result_address;
      })[0];

      // set marker (FOR DEBUGGING)
      // new Marker().setLngLat(e.result.center).addTo(map);

      if (feature) {
        const featureId = feature.id;
        const props = feature.properties;

        selectedCity = props.city;

        setActiveId(featureId, props, e.result.center);
        popup.setLngLat(e.result.center);
        popup.setHTML(renderTooltip(props)).addTo(map);

        // updateCityBoundary(selectedCity);
        renderBuildingResults(props);
        // renderCityResults(selectedCity);
      } else {
        if (result_place == 'Los Angeles') {
          buildingContainer.innerHTML = `<p>Building information for the city of Los Angeles is not available. Officials are currently auditing the city’s retrofit records after <a href=
          "https://www.latimes.com/california/story/2024-08-16/los-angeles-earthquake-retrofit-data-have-been-outdated-for-years">Times reporting uncovered numerous errors</a> in the city-provided data.</p>`;
        } else {
          buildingContainer.innerHTML = `<p><b>${result_address}</b> is not listed by a city government as meeting the criteria for needing a seismic retrofit or evaluation based on a recently passed ordinance. For more information, see the <a href="#faq">FAQ</a>.</p>`;

        }
      }
    });
  });
}
